import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import ImageUploader from "@/components/ImageUploader";
import { ArrowLeft, FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";

const CATEGORIES = [
    "Extension",
    "Renovation",
    "New Build",
    "Loft Conversion",
    "Kitchen",
    "Bathroom",
    "Brickwork",
    "Carpentry",
    "Roofing",
    "Plastering",
    "Landscaping",
    "Other",
];

export default function ProjectEditor() {
    const { id } = useParams();
    const isEdit = Boolean(id);
    const nav = useNavigate();
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [location, setLocation] = useState("");
    const [completedDate, setCompletedDate] = useState("");
    const [category, setCategory] = useState("Extension");
    const [images, setImages] = useState([]);
    const [coverImage, setCoverImage] = useState(null);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(isEdit);

    useEffect(() => {
        if (!isEdit) return;
        (async () => {
            try {
                const { data } = await api.get(`/projects/${id}`);
                const p = data.project;
                setTitle(p.title);
                setDescription(p.description || "");
                setLocation(p.location || "");
                setCompletedDate(p.completed_date || "");
                setCategory(p.category || "Extension");
                setImages(p.images || []);
                setCoverImage(p.cover_image || (p.images && p.images[0]) || null);
            } catch {
                toast.error("Failed to load project");
            } finally {
                setLoading(false);
            }
        })();
    }, [id, isEdit]);

    const submit = async (e) => {
        e.preventDefault();
        if (!title.trim()) return toast.error("Title is required");
        setSaving(true);
        const payload = {
            title,
            description,
            location,
            category,
            completed_date: completedDate || null,
            images,
            cover_image: coverImage || (images[0] ?? null),
        };
        try {
            if (isEdit) {
                await api.put(`/projects/${id}`, payload);
                toast.success("Project saved");
            } else {
                await api.post("/projects", payload);
                toast.success("Project created");
            }
            nav("/dashboard");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div className="max-w-5xl mx-auto px-6 py-16 text-overline text-neutral-500">Loading…</div>;
    }

    return (
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-12">
            <Link to="/dashboard" className="text-overline text-neutral-600 hover:text-black inline-flex items-center gap-2 mb-6">
                <ArrowLeft size={14} /> Back to dashboard
            </Link>

            <div className="text-overline text-[#E63946] mb-2">{isEdit ? "Edit project" : "New project"}</div>
            <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter leading-none uppercase mb-10">
                {isEdit ? "Update the work." : "Showcase a job."}
            </h1>

            <form onSubmit={submit} data-testid="project-form" className="grid lg:grid-cols-12 gap-10">
                <div className="lg:col-span-5 space-y-5">
                    <div>
                        <label className="text-overline text-neutral-700 block mb-2">Title *</label>
                        <input
                            required
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            className="input-field"
                            placeholder="e.g. Two-storey rear extension"
                            data-testid="project-title-input"
                        />
                    </div>

                    <div>
                        <label className="text-overline text-neutral-700 block mb-2">Category</label>
                        <select
                            value={category}
                            onChange={(e) => setCategory(e.target.value)}
                            className="input-field"
                            data-testid="project-category-input"
                        >
                            {CATEGORIES.map((c) => (
                                <option key={c}>{c}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="text-overline text-neutral-700 block mb-2">Location</label>
                        <input
                            value={location}
                            onChange={(e) => setLocation(e.target.value)}
                            className="input-field"
                            placeholder="Bristol, UK"
                            data-testid="project-location-input"
                        />
                    </div>

                    <div>
                        <label className="text-overline text-neutral-700 block mb-2">Completed date</label>
                        <input
                            type="date"
                            value={completedDate || ""}
                            onChange={(e) => setCompletedDate(e.target.value)}
                            className="input-field"
                            data-testid="project-date-input"
                        />
                    </div>

                    <div>
                        <label className="text-overline text-neutral-700 block mb-2">Description</label>
                        <textarea
                            rows={6}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            className="input-field resize-none"
                            placeholder="Describe the brief, the work and the result…"
                            data-testid="project-description-input"
                        />
                    </div>
                </div>

                <div className="lg:col-span-7">
                    <label className="text-overline text-neutral-700 block mb-3">Photos</label>
                    <ImageUploader images={images} setImages={setImages} max={20} />

                    {images.length > 0 && (
                        <div className="mt-6">
                            <label className="text-overline text-neutral-700 block mb-2">Cover photo</label>
                            <div className="flex gap-2 flex-wrap">
                                {images.map((p) => (
                                    <button
                                        type="button"
                                        key={p}
                                        onClick={() => setCoverImage(p)}
                                        data-testid={`set-cover-${p}`}
                                        className={`w-20 h-16 border-2 transition-all ${
                                            (coverImage || images[0]) === p
                                                ? "border-[#E63946] outline outline-1 outline-black outline-offset-2"
                                                : "border-transparent opacity-60 hover:opacity-100"
                                        }`}
                                    >
                                        <img
                                            src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`}
                                            alt=""
                                            className="w-full h-full object-cover"
                                        />
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <div className="lg:col-span-12 flex items-center gap-3 mt-2">
                    <button
                        type="submit"
                        disabled={saving}
                        className="btn-accent"
                        data-testid="project-save-btn"
                    >
                        <FloppyDisk size={16} weight="bold" /> {saving ? "Saving…" : isEdit ? "Save changes" : "Create project"}
                    </button>
                    <Link to="/dashboard" className="btn-ghost">
                        Cancel
                    </Link>
                </div>
            </form>
        </div>
    );
}
